"use client";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import AllergyCityCard from "../AllergyCityCard/AllergyCityCard";
import { useMediaQuery } from "@react-hook/media-query";
import { swiperClass } from "@/styles/cva/home.cva";

interface AllergyCitySectionProps {
  excludeId?: string;
  type?: string;
}

const AllergyCitySection: React.FC<AllergyCitySectionProps> = ({
  type,
  excludeId,
}) => {
  const IS_MOBILE_VIEW = useMediaQuery("(max-width: 1000px)");
  const pollutionCards = [
    {
      id: "least-us",
      title: "Least polluted in US",
      description:
        "View the least polluted cities in the US. Explore cities with the best air quality today!",
      link: "/us/least-polluted-cities",
      variant: "us",
    },
    {
      id: "most-us",
      title: "Most polluted in US",
      description:
        "View the most polluted cities in the US. Explore cities with the worst air quality today!",
      link: "/us/most-polluted-cities",
      variant: "us",
    },
    {
      id: "least-uk",
      title: "Least polluted in UK",
      description:
        "View the least polluted cities in the UK. Explore cities with the best air quality today!",
      link: "/uk/least-polluted-cities",
      variant: "uk",
    },
    {
      id: "most-uk",
      title: "Most polluted cities in UK",
      description:
        "View the most polluted cities in the UK. Explore UK pollution hotspots.",
      link: "/uk/most-polluted-cities",
      variant: "uk",
    },
  ] as const;

  const cityCards = [
    {
      id: "worst-us", // Unique ID
      title: "Worst pollen cities in US",
      description:
        "Discover the US cities with the highest pollen counts and learn how to manage allergies in these challenging environments.",
      link: "/us/cities-with-worst-pollen",
      variant: "us",
    },
    {
      id: "low-us",
      title: "Low-allergy cities in US",
      description:
        "Explore the best US cities for allergy sufferers, where pollen counts are consistently low, and breathing is easier.",
      link: "/us/cities-with-least-pollen",
      variant: "us",
    },
    {
      id: "low-uk",
      title: "Low-allergy cities in UK",
      description:
        "Learn about the UK cities with the lowest pollen counts and how they can provide relief for allergy sufferers.",
      link: "/uk/cities-with-least-pollen",
      variant: "uk",
    },
    {
      id: "worst-uk",
      title: "Worst pollen cities in UK",
      description:
        "Find out which UK cities are the toughest for allergy sufferers and get tips for coping with high pollen levels.",
      link: "/uk/cities-with-worst-pollen",
      variant: "uk",
    },
  ] as const;

  // Filter out the object with the exact id
  const filteredCityCards = excludeId
    ? (type == "aq" ? pollutionCards : cityCards).filter(
        (card) => card.id !== excludeId
      )
    : type == "aq"
    ? pollutionCards
    : cityCards;

  return (
    <section className="w-full">
      <div className="w-full mx-auto">
        {IS_MOBILE_VIEW ? (
          <div className="inline">
            <Swiper
              modules={[Autoplay, Pagination]}
              autoplay={{
                delay: 5000, // Auto slide every 5 seconds
                disableOnInteraction: false, // Keeps autoplay working even after user interaction
              }}
              pagination={{
                clickable: true,
              }}
              loop={true}
              className={swiperClass()}
            >
              {filteredCityCards.map((card, index) => (
                <SwiperSlide key={index}>
                  <AllergyCityCard
                    key={card.id}
                    title={card.title}
                    description={card.description}
                    link={card.link}
                    variant={card.variant}
                  />
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6 gap-x-8 self-stretch">
            {filteredCityCards.map((card) => (
              <AllergyCityCard
                key={card.id}
                title={card.title}
                description={card.description}
                link={card.link}
                variant={card.variant}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default AllergyCitySection;
